import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { CustomButton } from "components/CustomButton";
import { alignSchoolsSearchApi, districtHubListApi } from "../../api/api.service";
import {
  INDIVIDUAL,
  PRIVATE,
  US_STATES,
  plansForAudience,
  previewChargeCents,
  renewalPlanFor,
  termLabel,
} from "./draft";

const NOUN = { SCHOOL: "school", DISTRICT: "district", [INDIVIDUAL]: "teacher" };

const generateCode = () => {
  const letters = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 10; i += 1) out += letters[Math.floor(Math.random() * letters.length)];
  return out;
};

const perTerm = (plan) => (plan?.billingInterval === "MONTHLY" ? "month" : "year");

/**
 * State first, then the name: the same two steps a teacher takes at onboarding.
 * "Lincoln Elementary" is hundreds of schools nationally and a handful in one
 * state, and the state is what makes the list short enough to read.
 */
const OrgPicker = ({ audience, state, onState, org, onPick, onClear }) => {
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const noun = NOUN[audience];

  useEffect(() => {
    const q = text.trim();
    if (!state || q.length < 2) {
      setRows([]);
      setBusy(false);
      return undefined;
    }
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const res =
          audience === "SCHOOL" ? await alignSchoolsSearchApi({ q, state }) : await districtHubListApi({ Q: q, state });
        const list = res?.data ?? res?.districts ?? [];
        if (live) setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        if (live) {
          setRows([]);
          toast.error(e?.message || "The search failed.");
        }
      } finally {
        if (live) setBusy(false);
      }
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [text, state, audience]);

  if (org) {
    return (
      <div className="rc-picked">
        <div className="rc-picked-text">
          <strong>{org.name}</strong>
          {org.districtName && <span>{org.districtName}</span>}
        </div>
        <button type="button" className="rc-link" onClick={onClear}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="rc-org-picker">
      <div className="rc-field rc-field-state">
        <label htmlFor="rc-state">State</label>
        <select
          id="rc-state"
          className="form-select"
          value={state}
          onChange={(e) => {
            onState(e.target.value);
            setText("");
          }}
        >
          <option value="">Choose a state</option>
          {US_STATES.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </div>
      <div className="rc-field rc-field-grow">
        <label htmlFor="rc-org">{noun === "school" ? "School" : "District"}</label>
        <input
          id="rc-org"
          className="form-control"
          autoComplete="off"
          disabled={!state}
          placeholder={state ? `Start typing the ${noun}'s name` : "Choose a state first"}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      {state && text.trim().length >= 2 && (
        <div className="rc-results" role="listbox" aria-label={`Matching ${noun}s`}>
          {busy && <p className="rc-results-note">Searching</p>}
          {!busy && rows.length === 0 && <p className="rc-results-note">No {noun} by that name in this state.</p>}
          {!busy &&
            rows.map((row) => (
              <button
                key={row.id}
                type="button"
                role="option"
                aria-selected="false"
                className="rc-result"
                onClick={() => {
                  onPick(row);
                  setText("");
                }}
              >
                <strong>{row.name}</strong>
                {row.districtName && <span>{row.districtName}</span>}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};

/** A row of mutually exclusive choices that reads as one control. */
const Segments = ({ label, options, value, onChange, disabled }) => (
  <div className="rc-segments" role="radiogroup" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        role="radio"
        aria-checked={value === option.value}
        disabled={disabled || option.disabled}
        className={`rc-segment ${value === option.value ? "is-on" : ""}`}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

/** Two choices that each need a sentence to be understood. */
const ReachChoice = ({ on, title, text, onClick, disabled }) => (
  <button
    type="button"
    role="radio"
    aria-checked={on}
    disabled={disabled}
    className={`rc-reach ${on ? "is-on" : ""}`}
    onClick={onClick}
  >
    <strong>{title}</strong>
    <span>{text}</span>
  </button>
);

/** Opens from the choice that reveals it, so the new fields read as part of that choice. */
const Reveal = ({ open, children }) => (
  <div className={`rc-reveal ${open ? "is-open" : ""}`} aria-hidden={!open}>
    <div className="rc-reveal-inner">{open && children}</div>
  </div>
);

/** What the customer will see: the checkout's own panel, filled in as the form changes. */
export const CodePreview = ({ draft, plan, renewal, money }) => {
  const charge = previewChargeCents(draft, plan);
  const term = termLabel(draft, plan);
  const reduced = plan != null && charge != null && charge < plan.priceCents;
  const isCustomTerm = Boolean(String(draft.durationWeeks).trim() || draft.legacyMonths);
  const isTeacher = draft.audience === INDIVIDUAL;

  return (
    <div className="rc-preview" aria-label="What the customer sees at checkout">
      <p className="rc-preview-caption">What the {NOUN[draft.audience]} sees at checkout</p>
      <p className={`rc-preview-name ${draft.name.trim() ? "" : "is-empty"}`}>{draft.name.trim() || "Your code's name"}</p>

      {plan ? (
        <>
          <p className="rc-preview-plan">{plan.name}</p>
          <div className="rc-preview-price">
            {reduced && <s>{money(plan.priceCents)}</s>}
            <strong>{charge == null ? "—" : money(charge)}</strong>
          </div>
          <p className="rc-preview-term">for {isCustomTerm ? `the first ${term}` : `your first ${term.replace("1 ", "")}`}</p>
          {renewal && (
            <p className="rc-preview-after">
              {isTeacher
                ? `Then ${money(plan.priceCents)} per ${perTerm(plan)}.`
                : `Then renews automatically as ${renewal.name} at ${money(renewal.priceCents)} per ${perTerm(renewal)}, on the card they pay with. They can switch that off any time.`}
            </p>
          )}
        </>
      ) : (
        <p className="rc-preview-empty">Choose a plan to see the price.</p>
      )}
    </div>
  );
};

const CodeForm = ({ draft, setDraft, plans, money, saving, onSave, onCancel }) => {
  const [emailInput, setEmailInput] = useState("");
  const patch = (next) => setDraft({ ...draft, ...next });

  const isTeacher = draft.audience === INDIVIDUAL;
  const isPrivate = draft.visibility === PRIVATE;
  const locked = !!draft.locked;
  const noun = NOUN[draft.audience];
  const planOptions = plansForAudience(plans, draft.audience);
  const plan = plans.find((p) => String(p.id) === String(draft.planId));
  const renewal = renewalPlanFor(draft, plans, plan);
  const defaultRenewal = renewalPlanFor({ ...draft, renewalPlanId: "" }, plans, plan);

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    patch({ authorizedEmails: [...new Set([...draft.authorizedEmails, email])] });
    setEmailInput("");
  };

  const emails = (
    <div className="rc-field">
      <label htmlFor="rc-email">{isTeacher ? "Emails that can use it" : "Limit it to these emails (optional)"}</label>
      {draft.authorizedEmails.length > 0 && (
        <div className="rc-chips">
          {draft.authorizedEmails.map((email) => (
            <span key={email} className="rc-chip">
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                onClick={() => patch({ authorizedEmails: draft.authorizedEmails.filter((e) => e !== email) })}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="rc-inline">
        <input
          id="rc-email"
          className="form-control"
          type="email"
          placeholder="name@school.org"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
        />
        <CustomButton title="Add" color="light" onClick={addEmail} />
      </div>
    </div>
  );

  return (
    <div className="rc-form">
      <div className="rc-form-main">
        <h2 className="rc-title">{draft.id ? `Edit ${draft.code}` : "Create a subscription code"}</h2>
        {locked && (
          <p className="rc-note">
            This code has been used, so what it grants and who it reaches are fixed. You can still rename it, adjust its
            dates, raise its limit, or deactivate it.
          </p>
        )}

        <div className="rc-row">
          <div className="rc-field rc-field-grow">
            <label htmlFor="rc-name">Name</label>
            <input
              id="rc-name"
              className="form-control"
              maxLength={80}
              placeholder="Spring pilot program"
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </div>
          {!draft.id && (
            <div className="rc-field rc-field-code">
              <label htmlFor="rc-code">Code</label>
              <div className="rc-code-input">
                <input
                  id="rc-code"
                  className="form-control"
                  placeholder="Made for you"
                  value={draft.code}
                  onChange={(e) => patch({ code: e.target.value.toUpperCase() })}
                />
                <button type="button" className="rc-link" onClick={() => patch({ code: generateCode() })}>
                  Generate
                </button>
              </div>
            </div>
          )}
        </div>

        <section className="rc-group" aria-labelledby="rc-who">
          <h3 id="rc-who">Who can use it</h3>
          <Segments
            label="Kind of customer"
            value={draft.audience}
            disabled={locked}
            onChange={(audience) => patch({ audience, org: null, planId: "", renewalPlanId: "", state: "" })}
            options={[
              { value: "SCHOOL", label: "Schools", disabled: !!draft.id && isTeacher },
              { value: "DISTRICT", label: "Districts", disabled: !!draft.id && isTeacher },
              { value: INDIVIDUAL, label: "Teachers", disabled: !!draft.id },
            ]}
          />
          <div className="rc-reach-pair" role="radiogroup" aria-label="Public or private">
            <ReachChoice
              on={!isPrivate}
              disabled={locked}
              title="Public"
              text={`Any ${noun} with the code`}
              onClick={() => patch({ visibility: "PUBLIC", org: null })}
            />
            <ReachChoice
              on={isPrivate}
              disabled={locked}
              title="Private"
              text={isTeacher ? "Only the emails you list" : `Only one ${noun} you choose`}
              onClick={() => patch({ visibility: PRIVATE })}
            />
          </div>
          <Reveal open={isPrivate}>
            {!isTeacher && !locked && (
              <OrgPicker
                audience={draft.audience}
                state={draft.state}
                onState={(state) => patch({ state })}
                org={draft.org}
                onPick={(org) => patch({ org })}
                onClear={() => patch({ org: null })}
              />
            )}
            {!isTeacher && locked && <p className="rc-note">{draft.org?.name ?? "—"}</p>}
            {emails}
          </Reveal>
        </section>

        <section className="rc-group" aria-labelledby="rc-what">
          <h3 id="rc-what">What they get</h3>
          <div className="rc-field">
            <label htmlFor="rc-plan">Plan</label>
            <select
              id="rc-plan"
              className="form-select"
              disabled={locked}
              value={draft.planId}
              onChange={(e) => patch({ planId: e.target.value })}
            >
              <option value="">Choose a plan</option>
              {planOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}, {money(p.priceCents)} per {perTerm(p)}
                </option>
              ))}
            </select>
          </div>

          {isTeacher ? (
            <>
              <Segments
                label="Kind of discount"
                value={draft.discountType}
                disabled={locked}
                onChange={(discountType) => patch({ discountType })}
                options={[
                  { value: "AMOUNT", label: "Set the price" },
                  { value: "PERCENT", label: "Percentage off" },
                ]}
              />
              {draft.discountType === "PERCENT" ? (
                <div className="rc-field rc-field-half">
                  <label htmlFor="rc-percent">Percent off</label>
                  <div className="rc-affix">
                    <input
                      id="rc-percent"
                      className="form-control"
                      inputMode="numeric"
                      disabled={locked}
                      placeholder="20"
                      value={draft.percentOff}
                      onChange={(e) => patch({ percentOff: e.target.value })}
                    />
                    <span className="rc-suffix">%</span>
                  </div>
                </div>
              ) : (
                <div className="rc-field rc-field-half">
                  <label htmlFor="rc-price">Price for the first term</label>
                  <div className="rc-affix">
                    <span className="rc-prefix">$</span>
                    <input
                      id="rc-price"
                      className="form-control"
                      inputMode="decimal"
                      disabled={locked}
                      placeholder="49"
                      value={draft.price}
                      onChange={(e) => patch({ price: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="rc-row">
                <div className="rc-field rc-field-grow">
                  <label htmlFor="rc-price">Price</label>
                  <div className="rc-affix">
                    <span className="rc-prefix">$</span>
                    <input
                      id="rc-price"
                      className="form-control"
                      inputMode="decimal"
                      disabled={locked}
                      placeholder={plan ? `${plan.priceCents / 100}, the plan's price` : "500"}
                      value={draft.price}
                      onChange={(e) => patch({ price: e.target.value })}
                    />
                  </div>
                </div>
                <div className="rc-field rc-field-grow">
                  <label htmlFor="rc-weeks">Length</label>
                  <div className="rc-affix">
                    <input
                      id="rc-weeks"
                      className="form-control"
                      inputMode="numeric"
                      disabled={locked}
                      placeholder={draft.legacyMonths ? `${draft.legacyMonths} months, set before weeks` : "The plan's own term"}
                      value={draft.durationWeeks}
                      onChange={(e) => patch({ durationWeeks: e.target.value })}
                    />
                    <span className="rc-suffix">weeks</span>
                  </div>
                </div>
              </div>
              <div className="rc-field">
                <label htmlFor="rc-renewal">When it ends, renews as</label>
                <select
                  id="rc-renewal"
                  className="form-select"
                  disabled={locked}
                  value={draft.renewalPlanId}
                  onChange={(e) => patch({ renewalPlanId: e.target.value })}
                >
                  <option value="">
                    {defaultRenewal ? `${defaultRenewal.name}, ${money(defaultRenewal.priceCents)} per ${perTerm(defaultRenewal)}` : "Choose a plan first"}
                  </option>
                  {planOptions
                    .filter((p) => p.id !== defaultRenewal?.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}, {money(p.priceCents)} per {perTerm(p)}
                      </option>
                    ))}
                </select>
              </div>
            </>
          )}
        </section>

        <section className="rc-group" aria-labelledby="rc-limits">
          <h3 id="rc-limits">Limits</h3>
          <div className="rc-row">
            <div className="rc-field rc-field-narrow">
              <label htmlFor="rc-limit">Times it can be used</label>
              <input
                id="rc-limit"
                className="form-control"
                inputMode="numeric"
                value={draft.maxRedemptions}
                onChange={(e) => patch({ maxRedemptions: e.target.value })}
              />
            </div>
            <div className="rc-field rc-field-grow">
              <label htmlFor="rc-from">Starts</label>
              <input
                id="rc-from"
                className="form-control"
                type="date"
                value={draft.activatesAt}
                onChange={(e) => patch({ activatesAt: e.target.value })}
              />
            </div>
            <div className="rc-field rc-field-grow">
              <label htmlFor="rc-to">Ends</label>
              <input
                id="rc-to"
                className="form-control"
                type="date"
                value={draft.expiresAt}
                onChange={(e) => patch({ expiresAt: e.target.value })}
              />
            </div>
          </div>
          <p className="rc-hint">Dates apply across the US: the code works through the end of its last day everywhere.</p>
          {draft.id && (
            <label className="rc-check">
              <input type="checkbox" checked={draft.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />
              Active
            </label>
          )}
        </section>
      </div>

      <aside className="rc-form-side">
        <CodePreview draft={draft} plan={plan} renewal={renewal} money={money} />
        <div className="rc-actions">
          <CustomButton title="Cancel" color="light" disabled={saving} onClick={onCancel} />
          <CustomButton title={draft.id ? "Save changes" : "Create code"} color="primary" loading={saving} onClick={onSave} />
        </div>
      </aside>
    </div>
  );
};

export default CodeForm;
